// Copyright 2025-2026 Florian Gallon. All Rights reserved.
// Multi-tenancy utility functions for bakery management

import { db } from '../firebase';
import { collection, doc, getDoc, setDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';

// Get current bakery from subdomain or URL parameter
export const getCurrentBakeryId = () => {
  // Check for subdomain routing (e.g., bakery1.yourdomain.com -> bakery1)
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // If we have a subdomain (more than 2 parts), use it as bakeryId
  if (parts.length > 2 && parts[0] !== 'www') {
    return parts[0];
  }

  // Fallback: check URL parameters for bakeryId
  const urlParams = new URLSearchParams(window.location.search);
  const bakeryId = urlParams.get('bakeryId');

  // For localhost testing, also check for bakeryId in hash
  if (!bakeryId && window.location.hostname === 'localhost') {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    return hashParams.get('bakeryId');
  }

  return bakeryId;
};

// Create a new bakery for a baker
export const createBakery = async (bakerId, bakeryData) => {
  try {
    const bakeryRef = doc(collection(db, 'bakeries'), bakerId);
    const bakery = {
      id: bakerId,
      name: bakeryData.name || 'My Bakery',
      subdomain: bakeryData.subdomain || bakerId.toLowerCase().replace(/[^a-z0-9]/g, ''),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ownerId: bakerId,
      subscriptionStatus: 'grace_period', // New bakeries start in 7-day grace period
      ...bakeryData
    };

    await setDoc(bakeryRef, bakery);
    return bakery;
  } catch (error) {
    console.error('Error creating bakery:', error);
    throw error;
  }
};

// Get bakery by ID
export const getBakery = async (bakeryId) => {
  try {
    const bakeryRef = doc(db, 'bakeries', bakeryId);
    const bakerySnap = await getDoc(bakeryRef);

    if (bakerySnap.exists()) {
      return { id: bakerySnap.id, ...bakerySnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting bakery:', error);
    throw error;
  }
};

// Get bakery by subdomain
export const getBakeryBySubdomain = async (subdomain) => {
  try {
    const q = query(collection(db, 'bakeries'), where('subdomain', '==', subdomain));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting bakery by subdomain:', error);
    throw error;
  }
};

// Update bakery settings
export const updateBakery = async (bakeryId, updates) => {
  try {
    const bakeryRef = doc(db, 'bakeries', bakeryId);
    await setDoc(bakeryRef, updates, { merge: true });
  } catch (error) {
    console.error('Error updating bakery:', error);
    throw error;
  }
};

// Generate unique subdomain
export const generateUniqueSubdomain = async (baseName) => {
  let subdomain = baseName.toLowerCase().replace(/[^a-z0-9]/g, '');
  let counter = 0;
  let uniqueSubdomain = subdomain;

  while (await getBakeryBySubdomain(uniqueSubdomain)) {
    counter++;
    uniqueSubdomain = `${subdomain}${counter}`;
  }

  return uniqueSubdomain;
};

// ============================================
// Custom Domain Functions
// ============================================

// Get bakery by custom domain
export const getBakeryByCustomDomain = async (domain) => {
  try {
    const q = query(
      collection(db, 'bakeries'),
      where('customDomain', '==', domain),
      where('customDomainVerified', '==', true)
    );
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting bakery by custom domain:', error);
    return null;
  }
};

// Check if a custom domain is available (not used by another bakery)
export const isCustomDomainAvailable = async (domain, currentBakeryId = null) => {
  try {
    // Clean the domain (remove www. if present)
    const cleanDomain = domain.replace(/^www\./, '').toLowerCase();

    const q = query(
      collection(db, 'bakeries'),
      where('customDomain', '==', cleanDomain)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return true; // Domain is available
    }

    // Check if the domain belongs to the current bakery
    const existingBakery = snapshot.docs[0];
    return existingBakery.id === currentBakeryId;
  } catch (error) {
    console.error('Error checking custom domain availability:', error);
    return false;
  }
};

// Update custom domain for a bakery
export const updateCustomDomain = async (bakeryId, domain) => {
  try {
    const bakeryRef = doc(db, 'bakeries', bakeryId);

    if (!domain || domain.trim() === '') {
      // Remove custom domain
      await setDoc(bakeryRef, {
        customDomain: null,
        customDomainVerified: false,
        updatedAt: serverTimestamp()
      }, { merge: true });
      return { success: true };
    }

    // Clean and validate domain
    const cleanDomain = domain.replace(/^www\./, '').toLowerCase().trim();

    // Basic domain format validation
    const domainRegex = /^[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,}$/;
    if (!domainRegex.test(cleanDomain)) {
      return { success: false, error: 'Invalid domain format' };
    }

    // Check if domain is available
    const isAvailable = await isCustomDomainAvailable(cleanDomain, bakeryId);
    if (!isAvailable) {
      return { success: false, error: 'Domain is already in use by another bakery' };
    }

    // Update the bakery with the new custom domain (unverified initially)
    await setDoc(bakeryRef, {
      customDomain: cleanDomain,
      customDomainVerified: false,
      customDomainAddedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });

    return { success: true, domain: cleanDomain };
  } catch (error) {
    console.error('Error updating custom domain:', error);
    return { success: false, error: error.message };
  }
};

// Verify custom domain is properly configured
export const verifyCustomDomain = async (bakeryId) => {
  try {
    // Get the bakery to check its custom domain
    const bakery = await getBakery(bakeryId);
    if (!bakery || !bakery.customDomain) {
      return { success: false, error: 'No custom domain configured' };
    }

    const domain = bakery.customDomain;

    // Try to fetch from the custom domain to verify it's pointing to our servers
    // This is a simple check - the domain should load our app
    try {
      // Use a timeout for the fetch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      await fetch(`https://${domain}`, {
        method: 'HEAD',
        mode: 'no-cors', // We can't read the response, but we can check if it succeeds
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // If we get here without error, the domain is accessible
      // Mark it as verified in Firestore
      const bakeryRef = doc(db, 'bakeries', bakeryId);
      await setDoc(bakeryRef, {
        customDomainVerified: true,
        customDomainVerifiedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });

      return { success: true, message: 'Domain verified successfully' };
    } catch (fetchError) {
      // Domain is not yet accessible
      return {
        success: false,
        error: 'Domain not yet accessible. Please ensure DNS is configured correctly and wait for propagation.'
      };
    }
  } catch (error) {
    console.error('Error verifying custom domain:', error);
    return { success: false, error: error.message };
  }
};

// Remove custom domain from a bakery
export const removeCustomDomain = async (bakeryId) => {
  try {
    const bakeryRef = doc(db, 'bakeries', bakeryId);
    await setDoc(bakeryRef, {
      customDomain: null,
      customDomainVerified: false,
      customDomainAddedAt: null,
      customDomainVerifiedAt: null,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return { success: true };
  } catch (error) {
    console.error('Error removing custom domain:', error);
    return { success: false, error: error.message };
  }
};